// Where each tile of the line of play lies on the felt (table-local metres,
// table centre at the origin, −Z = North). Pure — no three.js — so it is
// unit-tested (tests/games/dominoes.test.ts).
//
// The line grows both ways from the first tile set: the right arm runs +X,
// the left arm −X. When an arm reaches the edge of the playing area it turns
// a corner (that tile lies across the turn) and runs back the other way one
// row further out (+Z for the right arm, −Z for the left), so a full
// double-six line snakes inside a ~0.4 m radius on the ~1.24 m table.
// Doubles lie crosswise, as at a real table.

export interface LineTile { tile: string; l: number; r: number }

/** One laid tile. `yaw` turns a tile whose long axis is +Z (inner pip at −Z) into place. */
export interface Laid {
  x: number; z: number; yaw: number;
  /** Pip toward the first tile (the matched end) and the outer pip. */
  inner: number; outer: number;
  double: boolean;
}

export interface LayoutOpts {
  /** Tile length and width (m). */
  L: number; W: number;
  /** Half-width of the playing area along X before an arm turns. */
  limit: number;
}

export const DEFAULT_LAYOUT: LayoutOpts = { L: 0.056, W: 0.028, limit: 0.33 };

type V = { x: number; z: number };

export function layoutLine(line: LineTile[], first: number, o: LayoutOpts = DEFAULT_LAYOUT): Laid[] {
  const out: Laid[] = new Array(line.length);
  if (!line.length) return out;
  const f = Math.max(0, Math.min(first, line.length - 1));
  const t0 = line[f];
  const d0 = t0.l === t0.r;
  // First tile: centred, running ±X (a double lies across).
  out[f] = { x: 0, z: 0, yaw: d0 ? 0 : Math.PI / 2, inner: t0.l, outer: t0.r, double: d0 };
  const half = (d0 ? o.W : o.L) / 2;
  const right: number[] = []; for (let i = f + 1; i < line.length; i++) right.push(i);
  const left: number[] = []; for (let i = f - 1; i >= 0; i--) left.push(i);
  walk(line, out, right, { x: half, z: 0 }, { x: 1, z: 0 }, { x: 0, z: 1 }, 'right', o);
  walk(line, out, left, { x: -half, z: 0 }, { x: -1, z: 0 }, { x: 0, z: -1 }, 'left', o);
  return out;
}

function walk(line: LineTile[], out: Laid[], idx: number[], p: V, d: V, n: V, arm: 'left' | 'right', o: LayoutOpts) {
  let P = { ...p }, D = { ...d };
  let afterCorner = false;
  const yawOf = (v: V) => Math.atan2(v.x, v.z);
  for (const i of idx) {
    const t = line[i];
    const inner = arm === 'right' ? t.l : t.r;
    const outer = arm === 'right' ? t.r : t.l;
    const dbl = t.l === t.r;
    // How far along the current direction this tile's far end would reach.
    const reach = (P.x + D.x * o.L) * D.x + (P.z + D.z * o.L) * D.z;
    if (!dbl && reach > o.limit) {
      // Corner: lay this tile across the turn, then run back one row out.
      out[i] = {
        x: P.x + D.x * (o.W / 2) + n.x * (o.L / 2 - o.W / 2),
        z: P.z + D.z * (o.W / 2) + n.z * (o.L / 2 - o.W / 2),
        yaw: yawOf(n), inner, outer, double: false,
      };
      // The next row starts against the corner tile's outer end; rows are a
      // tile length apart, so crosswise doubles in neighbouring rows never touch.
      P = { x: P.x + D.x * o.W + n.x * o.L, z: P.z + D.z * o.W + n.z * o.L };
      D = { x: -D.x, z: -D.z };
      afterCorner = true;
      continue;
    }
    // A double right after a corner lies along the run (crosswise it would hit the corner tile).
    if (dbl && !afterCorner) {
      out[i] = { x: P.x + D.x * (o.W / 2), z: P.z + D.z * (o.W / 2), yaw: yawOf(D) + Math.PI / 2, inner, outer, double: true };
      P = { x: P.x + D.x * o.W, z: P.z + D.z * o.W };
    } else {
      out[i] = { x: P.x + D.x * (o.L / 2), z: P.z + D.z * (o.L / 2), yaw: yawOf(D), inner, outer, double: dbl };
      P = { x: P.x + D.x * o.L, z: P.z + D.z * o.L };
    }
    afterCorner = false;
  }
}
